import flatten from 'lodash/flatten'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import type {ParserT} from '@freckle/parser'
import {record, string, nullable, Parser} from '@freckle/parser'
import type {AssignmentContextArg} from '@freckle/educator-entities/ts/common/helpers/assignment-context'
import {createAssignmentContextData} from '@freckle/educator-entities/ts/common/helpers/assignment-context'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import type {GenericAssignmentSessionAttrsT} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import {fromOpenApiSession} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {RlSkillUspIdT} from '@freckle/educator-entities/ts/common/types/rl-skill-usp-id'
import type {RlStandardIdT} from '@freckle/educator-entities/ts/common/types/rl-standard-id'
import type {ElaPassageT} from '@freckle/educator-entities/ts/ela/common/models/ela-passage'
import {
  fetchElaPassage,
  parseElaPassageShell,
} from '@freckle/educator-entities/ts/ela/common/models/ela-passage'
import type {GrammarQuestionT} from '@freckle/educator-entities/ts/ela/common/models/grammar-question'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {Either} from '@freckle/educator-entities/ts/types/either'
import type {SkillsForQuestionsT} from '@freckle/classroom/ts/ela/adaptive-skills/components/assign-skill-practice/assign-skill-form-container/selections'
import {fetchGrammarQuestion} from '../../grammar/collections/grammar-questions'

export async function createElaAdaptiveSkillsAssignment(
  rlDomainId: RlDomainIdT,
  elaStandardSetId: string,
  startsAt: number | null | undefined,
  studentIds: Array<number>,
  contextArg: AssignmentContextArg,
): Promise<GenericAssignmentSessionAttrsT> {
  const PATHS = Products.getPaths(Products.ElaAdaptiveSkillsPractice)
  return await fancyClient
    .POST('/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions', {
      params: {
        path: {
          'SubjectP-Subject': PATHS.subjectPath,
          'ProductP-AbbreviatedProduct': PATHS.productPath,
        },
        query: {
          'ela-standard-set-id': elaStandardSetId,
        },
      },
      body: {
        studentIds,
        metadata: {
          rlDomainId,
          standardSetId: elaStandardSetId,
        },
        context: createAssignmentContextData(contextArg),
        startsAt,
      },
    })
    .then(extractData)
    .then(data => fromOpenApiSession(data[0]!))
}

export type CreateElaTargetedParams = {
  contentIds?: Array<string>
  questionIds?: Array<string>
}

const createElaTargetedParamsToMetadata = ({contentIds, questionIds}: CreateElaTargetedParams) => ({
  ...(!isNil(contentIds) && !isEmpty(contentIds) ? {contentIds} : {}),
  ...(!isNil(questionIds) && !isEmpty(questionIds) ? {questionIds} : {}),
})

export async function createElaTargetedSkillsPracticeAssignment(
  rlSkillId: string | null,
  rlStandardId: RlStandardIdT | null,
  elaStandardSetId: string,
  skillsForQuestions: SkillsForQuestionsT,
  startsAt: number | null | undefined,
  params: CreateElaTargetedParams,
  studentIds: Array<number>,
  contextArg: AssignmentContextArg,
  title: string | undefined,
  unifiedAssignmentId?: string | null,
  unifiedCourseId?: number | null,
): Promise<GenericAssignmentSessionAttrsT> {
  const PATHS = Products.getPaths(Products.ElaTargetedSkillsPractice)
  return await fancyClient
    .POST('/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions', {
      params: {
        path: {
          'SubjectP-Subject': PATHS.subjectPath,
          'ProductP-AbbreviatedProduct': PATHS.productPath,
        },
        query: {
          'ela-standard-set-id': elaStandardSetId,
          ...(unifiedAssignmentId !== null && unifiedAssignmentId !== undefined
            ? {unified: true}
            : {}),
        },
      },
      body: {
        studentIds,
        startsAt,
        context: createAssignmentContextData(contextArg),
        metadata: {
          rlSkillId,
          rlStandardId: rlSkillId === null ? rlStandardId : null,
          standardSetId: elaStandardSetId,
          skillsForQuestions:
            skillsForQuestions !== undefined ? Object.fromEntries(skillsForQuestions) : {},
          ...createElaTargetedParamsToMetadata(params),
          title,
          ...(unifiedAssignmentId !== null && unifiedAssignmentId !== undefined
            ? {unifiedAssignmentId, unifiedCourseId}
            : {}),
        },
      },
    })
    .then(extractData)
    .then(data => fromOpenApiSession(data[0]!))
}

export const fetchTargetedSkillsPreviewPassages = async (
  targetSkillId: RlSkillUspIdT | null,
  targetStandardId: RlStandardIdT | null,
  excludeContent: Array<string>,
  elaStandardSetId: string,
  numQuestions: number,
): Promise<{passages: Array<ElaPassageT>; skillsForQuestions: SkillsForQuestionsT}> => {
  const response = await fancyClient
    .POST('/3/{ContentAreaP-ContentArea}/questions/arbitrary', {
      params: {
        path: {
          'ContentAreaP-ContentArea': 'ela',
        },
      },
      body: {
        targetSkillId,
        targetStandardId,
        excludedContent: excludeContent,
        standardSetId: elaStandardSetId,
        numQuestions,
      },
    })
    .then(extractData)

  const elaPassageShells = flatten(response.map(r => r.questions)).map(parseElaPassageShell)
  const elaPassageIds = elaPassageShells.map(({uuid}) => uuid)

  const passages = await Promise.all(elaPassageIds.map(fetchElaPassage))
  const skillsForQuestions = new Map<string, RlSkillUspIdT>()
  elaPassageShells.forEach(p => {
    if (!isNil(p.skillId)) skillsForQuestions.set(p.uuid, p.skillId)
  })

  return {passages, skillsForQuestions}
}

const elaQuestionShell: ParserT<{
  uuid: string
  skillId: RlSkillUspIdT | undefined | null
  answerType: string | undefined | null
}> = record({
  uuid: string(),
  skillId: nullable(string()),
  answerType: nullable(string()),
})

export const fetchElaQuestionsForSkill = async (
  targetSkillId: RlSkillUspIdT,
  targetStandardId: RlStandardIdT | null,
  elaStandardSetId: string,
  numQuestions: number,
): Promise<Either<ElaPassageT, GrammarQuestionT>[]> => {
  const response = await fancyClient
    .POST('/3/{ContentAreaP-ContentArea}/questions/arbitrary', {
      params: {
        path: {
          'ContentAreaP-ContentArea': 'ela',
        },
      },
      body: {
        targetSkillId,
        targetStandardId,
        standardSetId: elaStandardSetId,
        numQuestions,
      },
    })
    .then(extractData)

  const elaQuestionShells = flatten(response.map(r => r.questions)).map(
    Parser.mkRun(elaQuestionShell),
  )
  return await Promise.all(
    elaQuestionShells.map(async qs => {
      if (qs.answerType !== undefined && qs.answerType !== null) {
        const passage = await fetchElaPassage(qs.uuid)
        return Promise.resolve({tag: 'left', contents: passage})
      } else {
        const questions = await fetchGrammarQuestion(qs.uuid)
        return Promise.resolve({tag: 'right', contents: questions})
      }
    }),
  )
}
